import { Injectable, inject } from '@angular/core';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';
import { TSectionVisibilitySettings } from './export-body.model';
import { TExportData } from './utils/export-data';
import { EExportTypeName } from './utils/export-type-names-enum';

@Injectable({
  providedIn: 'root',
})
export class PointExportVisibilitySettingsService {
  private featureFlagService = inject(FeatureFlagService);

  getSectionVisibilitySettings(
    exportType: EExportTypeName,
    exportData: TExportData,
    customFieldIds: string[],
  ): TSectionVisibilitySettings {
    const sectionsVisibilitySettings = this.getSectionVisibilitySettingsBase(
      exportType,
      exportData,
      customFieldIds,
    );
    const extraSettings = this.getExtraSettings(exportType, exportData);
    const mergedSettings = Object.assign(sectionsVisibilitySettings, extraSettings);

    if (this.featureFlagService.getFlag(EFeatureFlag.RELATED_POINTS)) {
      return mergedSettings;
    }

    const { showRelationships, ...settingsWithoutRelationships } = mergedSettings;
    return settingsWithoutRelationships;
  }

  private getExtraSettings(
    exportType: EExportTypeName,
    exportData: TExportData,
  ): TSectionVisibilitySettings {
    if (
      exportType === EExportTypeName.ZIP ||
      exportType === EExportTypeName.PDF ||
      exportType === EExportTypeName.PDF_ZIP
    ) {
      return this.getIndividualReportSettings(exportData);
    } else if (exportType === EExportTypeName.WORD || exportType === EExportTypeName.ZIP_WORD) {
      return this.getWordSettings(exportData);
    } else if (exportType === EExportTypeName.DETAILED_LIST) {
      return this.getDetailedListSettings(exportData);
    }
    return {};
  }

  private getDetailedListSettings(exportData: TExportData): TSectionVisibilitySettings {
    return {
      showExportBy: exportData.showExportBy,
      showSiteMap: exportData.showSiteMap,
      showAssignees: exportData.showAssignees,
      showCreatedBy: exportData.showCreatedBy,
      showSignature: exportData.showSignature,
      showRelationships: exportData.showRelationships,
      showFlagged: exportData.showFlagged,
    };
  }

  private getWordSettings(exportData: TExportData): TSectionVisibilitySettings {
    return {
      showEditDate: exportData.showEditDate,
      showDocuments: exportData.showDocuments,
      showRelationships: exportData.showRelationships,
    };
  }

  private getIndividualReportSettings(exportData: TExportData): TSectionVisibilitySettings {
    return {
      showExportBy: exportData.showExportBy,
      showSiteMap: exportData.showSiteMap,
      showAssignees: exportData.showAssignees,
      showCreatedBy: exportData.showCreatedBy,
      showSignature: exportData.showSignature,
      showActivities: exportData.showActivities,
      showDocuments: exportData.showDocuments,
      showFlagged: exportData.showFlagged,
      showRelationships: exportData.showRelationships,
    };
  }

  private getSimpleListSettings(exportData: TExportData): TSectionVisibilitySettings {
    return {
      showSignature: exportData.showSignature,
      showUrl: exportData.showUrl,
      showRelationships: exportData.showRelationships,
    };
  }

  private getSectionVisibilitySettingsBase(
    exportType: EExportTypeName,
    exportData: TExportData,
    customFieldIds: string[],
  ): TSectionVisibilitySettings {
    if (exportType === EExportTypeName.SIMPLE_LIST) {
      return this.getSimpleListSettings(exportData);
    } else {
      const settings: TSectionVisibilitySettings = {
        showId: exportData.showId,
        showTitle: exportData.showTitle,
        showDescription: exportData.showDescription,
        showPriority: exportData.showPriority,
        showStatus: exportData.showStatus,
        showTags: exportData.showTags,
        showCustomFields: exportData.showCustomFields,
        showCreationDate: exportData.showCreationDate,
        showComments: exportData.showComments,
        showExportDate: exportData.showExportDate,
        showImages: exportData.showImages,
        imagesSize: exportData.imagesSize,
        showFlagged: exportData.showFlagged,
        customFieldIds: customFieldIds ? customFieldIds.map((id) => id.toString()) : [],
        showUrl: exportData.showUrl,
        showRelationships: exportData.showRelationships,
        filterCommentsByUpdatedDate: exportData.filterCommentsByUpdatedDate,
        filterImagesByUpdatedDate: exportData.filterImagesByUpdatedDate,
      };

      return settings;
    }
  }
}
